<template>
  <div class="admin-page">
    <div class="nav-bar">
      <div class="nav-title">管理员控制台</div>
      <div class="auth-buttons">
        <div class="auth-button">{{ currentUser.username }}</div>
        <div class="auth-button" @click="handleLogout">退出</div>
      </div>
    </div>
    
    <div class="admin-content">
      <!-- 标签切换 -->
      <div class="admin-tabs">
        <div 
          class="tab" 
          :class="{ active: activeTab === 'activation' }"
          @click="activeTab = 'activation'"
        >
          激活码管理
        </div>
        <div 
          class="tab" 
          :class="{ active: activeTab === 'articles' }"
          @click="activeTab = 'articles'"
        >
          文章管理
        </div>
      </div>
      
      <!-- 激活码管理 -->
      <div v-if="activeTab === 'activation'">
        <div class="admin-header">
          <h2>激活码管理</h2>
          <div class="admin-actions">
            <div class="quantity-input">
              <label>数量：</label>
              <select v-model="generateCount">
                <option value="1">1</option>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <button class="action-button" @click="generateCodes">生成激活码</button>
          </div>
        </div>
        
        <div v-if="generatedCodes.length > 0" class="generated-codes">
          <h3>新生成的激活码</h3>
          <div class="code-list">
            <div v-for="(code, index) in generatedCodes" :key="index" class="code-item">
              <div class="code-value">{{ code.code }}</div>
              <button class="copy-button" @click="copyCode(code.code)">复制</button>
            </div>
          </div>
          <button class="action-button" @click="generatedCodes = []">清除</button>
        </div>
        
        <div class="activation-codes">
          <div class="codes-header">
            <h3>激活码列表</h3>
            <button class="refresh-button" @click="refreshCodes">刷新</button>
          </div>
          
          <div v-if="activationCodesLoading" class="loading">
            加载中...
          </div>
          
          <div v-else-if="activationCodes.length === 0" class="no-codes">
            暂无激活码
          </div>
          
          <div v-else class="codes-table">
            <table>
              <thead>
                <tr>
                  <th>激活码</th>
                  <th>状态</th>
                  <th>创建时间</th>
                  <th>过期时间</th>
                  <th>使用人</th>
                  <th>使用时间</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="code in activationCodes" :key="code._id || code.id">
                  <td>{{ code.code }}</td>
                  <td>{{ code.isUsed ? '已使用' : '未使用' }}</td>
                  <td>{{ formatDate(code.createdAt) }}</td>
                  <td>{{ formatDate(code.expiresAt) }}</td>
                  <td>{{ code.usedBy ? code.usedBy : '-' }}</td>
                  <td>{{ code.usedAt ? formatDate(code.usedAt) : '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      <!-- 文章管理 -->
      <div v-if="activeTab === 'articles'">
        <div class="admin-header">
          <h2>文章管理</h2>
          <button class="action-button" @click="showArticleForm = true">发布新文章</button>
        </div>
        
        <!-- 文章表单 -->
        <div v-if="showArticleForm" class="article-form-container">
          <h3>{{ editingArticle ? '编辑文章' : '发布新文章' }}</h3>
          
          <div class="form-group">
            <label for="title">标题</label>
            <input 
              type="text" 
              id="title" 
              v-model="articleForm.title" 
              placeholder="请输入文章标题"
              required
            />
          </div>
          
          <div class="form-group">
            <label for="author">作者</label>
            <input 
              type="text" 
              id="author" 
              v-model="articleForm.author" 
              placeholder="请输入作者名称"
            />
          </div>
          
          <div class="form-group">
            <label for="content">正文内容</label>
            <textarea 
              id="content" 
              v-model="articleForm.content" 
              placeholder="请输入文章内容（支持HTML格式）"
              rows="10"
              required
            ></textarea>
          </div>
          
          <div class="form-actions">
            <button class="cancel-button" @click="cancelArticleForm">取消</button>
            <button class="submit-button" @click="submitArticle">发布</button>
          </div>
        </div>
        
        <!-- 文章列表 -->
        <div v-else class="articles-container">
          <div class="articles-header">
            <h3>文章列表</h3>
            <button class="refresh-button" @click="fetchArticles">刷新</button>
          </div>
          
          <div v-if="articlesLoading" class="loading">
            加载中...
          </div>
          
          <div v-else-if="articles.length === 0" class="no-articles">
            暂无文章，请点击"发布新文章"按钮添加文章。
          </div>
          
          <div v-else class="articles-table">
            <table>
              <thead>
                <tr>
                  <th>标题</th>
                  <th>作者</th>
                  <th>发布时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="article in articles" :key="article._id || article.id">
                  <td>{{ article.title }}</td>
                  <td>{{ article.author }}</td>
                  <td>{{ formatDate(article.createdAt) }}</td>
                  <td class="actions-cell">
                    <button class="edit-button" @click="editArticle(article)">编辑</button>
                    <button class="delete-button" @click="deleteArticle(article._id || article.id)">删除</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import axios from 'axios'

export default {
  name: 'AdminPage',
  setup() {
    const store = useStore()
    const router = useRouter()
    
    const currentUser = computed(() => store.getters.currentUser)
    const isAdmin = computed(() => store.getters.isAdmin)
    const activationCodes = computed(() => store.getters.activationCodes)
    const activationCodesLoading = computed(() => store.getters.activationCodesLoading)
    
    const generateCount = ref(1)
    const generatedCodes = ref([])
    const activeTab = ref('activation')
    
    // 文章相关
    const articles = ref([])
    const articlesLoading = ref(false)
    const showArticleForm = ref(false)
    const editingArticle = ref(null)
    const articleForm = ref({
      title: '',
      content: '',
      author: '管理员'
    })
    
    // 检查是否是管理员，如果不是则重定向
    onMounted(async () => {
      await store.dispatch('checkAuth')
      
      if (!isAdmin.value) {
        router.push('/listen')
      }
      
      refreshCodes()
      fetchArticles()
    })
    
    const handleLogout = () => {
      store.commit('logout')
      router.push('/login')
    }
    
    // 激活码相关方法
    const generateCodes = async () => {
      const codes = await store.dispatch('generateActivationCodes', parseInt(generateCount.value))
      if (codes) {
        generatedCodes.value = codes
      }
    }
    
    const refreshCodes = async () => {
      await store.dispatch('getActivationCodes')
    }
    
    const copyCode = (code) => {
      navigator.clipboard.writeText(code)
        .then(() => {
          alert('激活码已复制到剪贴板')
        })
        .catch(err => {
          console.error('复制失败:', err)
          alert('复制失败，请手动复制')
        })
    }
    
    // 文章相关方法
    const fetchArticles = async () => {
      try {
        articlesLoading.value = true
        const response = await axios.get('/api/articles')
        articles.value = response.data
      } catch (error) {
        console.error('获取文章列表失败:', error)
        alert('获取文章列表失败，请稍后重试')
      } finally {
        articlesLoading.value = false
      }
    }
    
    const submitArticle = async () => {
      try {
        if (!articleForm.value.title || !articleForm.value.content) {
          alert('请填写完整的文章信息')
          return
        }
        
        const formData = { ...articleForm.value }
        
        if (editingArticle.value) {
          // 编辑现有文章
          const articleId = editingArticle.value._id || editingArticle.value.id
          await axios.put(`/api/articles/${articleId}`, formData, {
            headers: { 'x-auth-token': store.state.token }
          })
          alert('文章已成功更新')
        } else {
          // 发布新文章
          await axios.post('/api/articles', formData, {
            headers: { 'x-auth-token': store.state.token }
          })
          alert('文章已成功发布')
        }
        
        // 重置表单
        resetArticleForm()
        
        // 重新获取文章列表
        fetchArticles()
      } catch (error) {
        console.error('发布文章失败:', error)
        alert('发布文章失败，请稍后重试')
      }
    }
    
    const editArticle = (article) => {
      editingArticle.value = article
      
      // 获取完整的文章内容
      const fetchArticleContent = async () => {
        try {
          const articleId = article._id || article.id
          const response = await axios.get(`/api/articles/${articleId}`)
          const fullArticle = response.data
          
          editingArticle.value = fullArticle
          articleForm.value = {
            title: fullArticle.title,
            content: fullArticle.content,
            author: fullArticle.author || '管理员'
          }
        } catch (error) {
          console.error('获取文章详情失败:', error)
          // 如果获取详情失败，仍使用列表中的基本信息
          articleForm.value = {
            title: article.title,
            content: article.content || '',
            author: article.author || '管理员'
          }
        }
      }
      
      fetchArticleContent()
      showArticleForm.value = true
    }
    
    const deleteArticle = async (id) => {
      if (confirm('确定要删除这篇文章吗？此操作不可恢复。')) {
        try {
          await axios.delete(`/api/articles/${id}`, {
            headers: { 'x-auth-token': store.state.token }
          })
          alert('文章已成功删除')
          fetchArticles() // 重新获取文章列表
        } catch (error) {
          console.error('删除文章失败:', error)
          alert('删除文章失败，请稍后重试')
        }
      }
    }
    
    const viewArticle = (id) => {
      // 在新窗口打开文章
      window.open(`/about-us?article=${id}`, '_blank')
    }
    
    const cancelArticleForm = () => {
      resetArticleForm()
    }
    
    const resetArticleForm = () => {
      articleForm.value = {
        title: '',
        content: '',
        author: '管理员'
      }
      editingArticle.value = null
      showArticleForm.value = false
    }
    
    const formatDate = (dateString) => {
      if (!dateString) return '-'
      
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
    
    return {
      currentUser,
      activationCodes,
      activationCodesLoading,
      generateCount,
      generatedCodes,
      activeTab,
      articles,
      articlesLoading,
      showArticleForm,
      editingArticle,
      articleForm,
      handleLogout,
      generateCodes,
      refreshCodes,
      copyCode,
      fetchArticles,
      submitArticle,
      editArticle,
      deleteArticle,
      viewArticle,
      cancelArticleForm,
      formatDate
    }
  }
}
</script>

<style scoped>
.admin-page {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
}

.nav-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: #2c3e50;
  color: white;
}

.nav-title {
  font-size: 20px;
  font-weight: bold;
}

.auth-buttons {
  display: flex;
  gap: 20px;
}

.auth-button {
  cursor: pointer;
  font-size: 16px;
}

.admin-content {
  flex: 1;
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

/* 标签样式 */
.admin-tabs {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
}

.tab {
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
}

.tab.active {
  background-color: #2c3e50;
  color: white;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.admin-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.quantity-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

select {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.action-button {
  background-color: #2c3e50;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
}

.generated-codes {
  margin-bottom: 24px;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.code-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 16px 0;
}

.code-item {
  display: flex;
  gap: 8px;
  align-items: center;
  background-color: #f0f0f0;
  padding: 8px 12px;
  border-radius: 4px;
}

.code-value {
  font-family: monospace;
  font-size: 16px;
}

.copy-button {
  background-color: transparent;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
}

.activation-codes, .articles-container {
  background-color: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.codes-header, .articles-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.refresh-button {
  background-color: transparent;
  border: 1px solid #2c3e50;
  color: #2c3e50;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
}

.loading, .no-codes, .no-articles {
  text-align: center;
  padding: 24px;
  color: #666;
}

.codes-table, .articles-table {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  text-align: left;
  padding: 12px;
  border-bottom: 1px solid #eee;
}

th {
  background-color: #f8f9fa;
  font-weight: 600;
}

tr:hover {
  background-color: #f8f9fa;
}

/* 文章表单样式 */
.article-form-container {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-group input, .form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.cancel-button {
  background-color: #e0e0e0;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
}

.submit-button {
  background-color: #2c3e50;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
}

.actions-cell {
  display: flex;
  gap: 8px;
}

.edit-button, .delete-button {
  background-color: transparent;
  border: 1px solid #2c3e50;
  color: #2c3e50;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 14px;
}

.delete-button {
  border-color: #dc3545;
  color: #dc3545;
}

.delete-button:hover {
  background-color: #dc3545;
  color: white;
}

.view-button {
  border-color: #4CAF50;
  color: #4CAF50;
}
</style> 